"use client"

import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import {
    EnvelopeIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    ClockIcon,
    PaperAirplaneIcon,
    CheckCircleIcon,
    XCircleIcon,
    BriefcaseIcon,
    UserGroupIcon,
    TrophyIcon,
} from "@heroicons/react/24/outline"
import {
    EnvelopeIcon as EnvelopeIconSolid,
    PhoneIcon as PhoneIconSolid,
    MapPinIcon as MapPinIconSolid,
} from "@heroicons/react/24/solid"
import { addContactUsPublicAsync } from "./ContactUsHomeSlice"
import { Footer } from "../footer/footer"

// Chakra UI-style Toast Component
const Toast = ({ message, type, onClose }) => {
    return (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
            <div
                className={`flex items-center p-4 rounded-lg shadow-lg border max-w-md ${type === "success"
                    ? "bg-white border-green-200 text-green-800"
                    : "bg-white border-red-200 text-red-800"
                    }`}
            >
                {type === "success" ? (
                    <CheckCircleIcon className="h-5 w-5 mr-3 text-green-600 flex-shrink-0" />
                ) : (
                    <XCircleIcon className="h-5 w-5 mr-3 text-red-600 flex-shrink-0" />
                )}
                <p className="font-medium text-sm">{message}</p>
                <button
                    onClick={onClose}
                    className="ml-4 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                >
                    <XCircleIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    )
}

const ContactUsHome = () => {
    const dispatch = useDispatch()
    const [formData, setFormData] = useState({
        user_type: "candidate",
        type: "contact",
        name: "",
        subject: "",
        email: "",
        mobile: "",
        description: "",
    })

    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [toast, setToast] = useState(null)
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const showToast = (message, type) => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 5000)
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.name.trim()) {
            newErrors.name = "Full name is required"
        } else if (formData.name.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters"
        }

        if (!formData.subject.trim()) {
            newErrors.subject = "Subject is required"
        } else if (formData.subject.trim().length < 5) {
            newErrors.subject = "Subject must be at least 5 characters"
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email address is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address"
        }

        if (!formData.mobile.trim()) {
            newErrors.mobile = "Mobile number is required"
        } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ""))) {
            newErrors.mobile = "Please enter a valid 10-digit mobile number"
        }

        if (!formData.description.trim()) {
            newErrors.description = "Message is required"
        } else if (formData.description.trim().length < 10) {
            newErrors.description = "Message must be at least 10 characters"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            showToast("Please fix the errors in the form", "error")
            return
        }

        setIsSubmitting(true)

        try {
            await dispatch(addContactUsPublicAsync(formData)).unwrap()
            showToast("Thank you! Your message has been sent successfully. We'll get back to you soon.", "success")
            setFormData({
                user_type: "candidate",
                type: "contact",
                name: "",
                subject: "",
                email: "",
                mobile: "",
                description: "",
            })
        } catch (error) {
            showToast("Sorry, there was an error sending your message. Please try again.", "error")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            <div className="min-h-screen bg-gray-50">
                {/* Header */}


                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Left Side - Content */}
                        <div className="space-y-8">
                            <div>
                                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                    Unlock your career potential
                                </h2>
                                <p className="text-lg text-gray-600">
                                    Connect with top employers and discover opportunities that match your skills and aspirations.
                                </p>
                            </div>

                            {/* Features */}
                            <div className="space-y-8">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <BriefcaseIcon className="h-6 w-6 text-blue-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            Find Your Dream Job
                                        </h3>
                                        <p className="text-gray-600">
                                            Access thousands of job opportunities from leading companies across India.
                                            Filter by location, salary, and experience level.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <UserGroupIcon className="h-6 w-6 text-blue-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            Connect with Recruiters
                                        </h3>
                                        <p className="text-gray-600">
                                            Build meaningful connections with hiring managers and recruiters.
                                            Get personalized job recommendations based on your profile.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <TrophyIcon className="h-6 w-6 text-blue-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            Advance Your Career
                                        </h3>
                                        <p className="text-gray-600">
                                            Access career guidance, skill assessments, and professional development
                                            resources to accelerate your career growth.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Contact Form */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Get in Touch</h3>
                                <p className="text-gray-600">
                                    Have questions? We're here to help you succeed in your career journey.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Inquiry Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        How can we help you?
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <label className="relative">
                                            <input
                                                type="radio"
                                                name="type"
                                                value="contact"
                                                checked={formData.type === "contact"}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div
                                                className={`p-3 border rounded-lg cursor-pointer transition-all text-center ${formData.type === "contact"
                                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                                    : "border-gray-300 hover:border-gray-400"
                                                    }`}
                                            >
                                                <ChatBubbleLeftRightIcon className="h-5 w-5 mx-auto mb-1" />
                                                <div className="text-sm font-medium">General Inquiry</div>
                                            </div>
                                        </label>
                                        <label className="relative">
                                            <input
                                                type="radio"
                                                name="type"
                                                value="sales"
                                                checked={formData.type === "sales"}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div
                                                className={`p-3 border rounded-lg cursor-pointer transition-all text-center ${formData.type === "sales"
                                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                                    : "border-gray-300 hover:border-gray-400"
                                                    }`}
                                            >
                                                <DocumentTextIcon className="h-5 w-5 mx-auto mb-1" />
                                                <div className="text-sm font-medium">Career Support</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.name ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Your name"
                                    />
                                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.email ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Your email"
                                    />
                                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                                </div>

                                {/* Subject */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.subject ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="What's this about?"
                                    />
                                    {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                                </div>

                                {/* Mobile */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Mobile Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.mobile ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Your mobile number"
                                    />
                                    {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${errors.description ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Tell us how we can help you..."
                                    />
                                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium transition-colors ${isSubmitting
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Sending...
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center">
                                            <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                                            Send Message
                                        </div>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Trusted By Section */}

                </div>
                <Footer />
            </div>
        </>
    )
}

export default ContactUsHome